import { useState, useEffect, useCallback } from 'react';

export type PageType =
  | { type: 'home' }
  | { type: 'section'; id: string }
  | { type: 'topic'; sectionId: string; topicId: string }
  | { type: 'hi-home' }
  | { type: 'hi-section'; id: string }
  | { type: 'hi-topic'; sectionId: string; topicId: string };

export type AppType = 'main' | 'health-insurance';

function parseUrl(path: string): { app: AppType; page: PageType } {
  // /hi/topic/hi-3.2
  const hiTopicMatch = path.match(/^\/hi\/topic\/(hi-[\w.-]+)$/);
  if (hiTopicMatch) {
    const topicId = hiTopicMatch[1];
    // Derive sectionId: "hi-3.2" → "hi-3"
    const sectionId = topicId.replace(/\.\d+$/, '');
    return { app: 'health-insurance', page: { type: 'hi-topic', sectionId, topicId } };
  }

  // /hi/section/hi-2
  const hiSectionMatch = path.match(/^\/hi\/section\/(hi-[\w-]+)$/);
  if (hiSectionMatch) {
    return { app: 'health-insurance', page: { type: 'hi-section', id: hiSectionMatch[1] } };
  }

  // /hi
  if (path === '/hi' || path === '/hi/') {
    return { app: 'health-insurance', page: { type: 'hi-home' } };
  }

  // /topic/3.2
  const topicMatch = path.match(/^\/topic\/([\w.-]+)$/);
  if (topicMatch) {
    const topicId = topicMatch[1];
    // Derive sectionId: "3.2" → "3", "13.5" → "13"
    const sectionId = topicId.replace(/\.\d+$/, '');
    return { app: 'main', page: { type: 'topic', sectionId, topicId } };
  }

  // /section/2
  const sectionMatch = path.match(/^\/section\/([\w-]+)$/);
  if (sectionMatch) {
    return { app: 'main', page: { type: 'section', id: sectionMatch[1] } };
  }

  // / (home)
  return { app: 'main', page: { type: 'home' } };
}

function pageToUrl(_app: AppType, page: PageType): string {
  switch (page.type) {
    case 'home':
      return '/';
    case 'section':
      return `/section/${page.id}`;
    case 'topic':
      return `/topic/${page.topicId}`;
    case 'hi-home':
      return '/hi';
    case 'hi-section':
      return `/hi/section/${page.id}`;
    case 'hi-topic':
      return `/hi/topic/${page.topicId}`;
  }
}

export function useNavigation() {
  const initial = parseUrl(window.location.pathname);
  const [currentApp, setCurrentApp] = useState<AppType>(initial.app);
  const [currentPage, setCurrentPage] = useState<PageType>(initial.page);

  // Listen for popstate (browser back/forward)
  useEffect(() => {
    const handlePopState = () => {
      const { app, page } = parseUrl(window.location.pathname);
      setCurrentApp(app);
      setCurrentPage(page);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = useCallback((app: AppType, page: PageType) => {
    const url = pageToUrl(app, page);
    window.history.pushState(null, '', url);
    setCurrentApp(app);
    setCurrentPage(page);
    window.scrollTo(0, 0);
  }, []);

  // Convenience helpers
  const goHome = useCallback(() => navigate('main', { type: 'home' }), [navigate]);
  const goHIHome = useCallback(() => navigate('health-insurance', { type: 'hi-home' }), [navigate]);
  const goToSection = useCallback((id: string) => navigate('main', { type: 'section', id }), [navigate]);
  const goToTopic = useCallback((sectionId: string, topicId: string) => navigate('main', { type: 'topic', sectionId, topicId }), [navigate]);
  const goToHISection = useCallback((id: string) => navigate('health-insurance', { type: 'hi-section', id }), [navigate]);
  const goToHITopic = useCallback((sectionId: string, topicId: string) => navigate('health-insurance', { type: 'hi-topic', sectionId, topicId }), [navigate]);

  // Sidebar helpers
  const getCurrentSectionId = (): string | null => {
    if (currentPage.type === 'section') return currentPage.id;
    if (currentPage.type === 'topic') return currentPage.sectionId;
    if (currentPage.type === 'hi-section') return currentPage.id;
    if (currentPage.type === 'hi-topic') return currentPage.sectionId;
    return null;
  };

  const getCurrentTopicId = (): string | null => {
    if (currentPage.type === 'topic') return currentPage.topicId;
    if (currentPage.type === 'hi-topic') return currentPage.topicId;
    return null;
  };

  return {
    currentApp,
    currentPage,
    navigate,
    goHome,
    goHIHome,
    goToSection,
    goToTopic,
    goToHISection,
    goToHITopic,
    getCurrentSectionId,
    getCurrentTopicId,
  };
}
